import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Select,
  HStack,
  VStack,
  Button,
  Badge,
  IconButton,
  useToast,
  Spinner,
  Center,
  Text,
  InputGroup,
  InputLeftElement,
  Tooltip,
  useDisclosure,
  Flex,
  Spacer
} from '@chakra-ui/react';
import { 
  FiSearch, 
  FiEye, 
  FiRefreshCw, 
  FiFilter,
  FiDownload,
  FiChevronLeft,
  FiChevronRight
} from 'react-icons/fi';
import api from '../../../../../services/api';
import EmailDetailsModal from './EmailDetailsModal';

const ForwardingHistory = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    startDate: '',
    endDate: '',
    priority: ''
  });
  const [selectedEmail, setSelectedEmail] = useState(null);
  
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 50,
        ...filters
      };

      // Remove empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await api.get('/email-forwarding/history', { params });
      
      console.log('Fetched emails response:', response.data);
      // Check if response.data is an object with data property or the array directly
      const emailData = response.data.data !== undefined ? response.data.data : response.data;
      console.log('Setting emails:', emailData.length, 'emails');
      setEmails(emailData);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast({
        title: 'Erro ao carregar histórico',
        description: error.response?.data?.message || 'Erro ao buscar emails',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  }, [page, filters, toast]);

  useEffect(() => {
    console.log('Current emails state:', emails);
    console.log('Emails length:', emails.length);
  }, [emails]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const handleStatusUpdate = async (emailId, newStatus, notes = '') => {
    try {
      await api.put(`/email-forwarding/history/${emailId}/status`, {
        status: newStatus,
        notes
      });

      toast({
        title: 'Status atualizado',
        description: 'O status do email foi atualizado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchEmails(); // Refresh the list
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Erro ao atualizar status',
        description: error.response?.data?.message || 'Erro ao atualizar',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const viewEmailDetails = async (emailId) => {
    console.log('viewEmailDetails called with emailId:', emailId);
    try {
      const response = await api.get(`/email-forwarding/history/${emailId}`);
      console.log('Email details response:', response.data);
      
      // Check if response has data property or is the data directly
      const emailDetails = response.data.data !== undefined ? response.data.data : response.data;
      console.log('Setting selectedEmail:', emailDetails);
      setSelectedEmail(emailDetails);
      
      console.log('Opening modal...');
      onOpen();
    } catch (error) {
      console.error('Error fetching email details:', error);
      toast({
        title: 'Erro ao carregar detalhes',
        description: error.response?.data?.message || 'Erro ao buscar detalhes',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'replied': return 'green';
      case 'ignored': return 'gray';
      case 'in_progress': return 'blue';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendente';
      case 'replied': return 'Respondido';
      case 'ignored': return 'Ignorado';
      case 'in_progress': return 'Em Progresso';
      default: return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'red';
      case 'high': return 'orange';
      case 'normal': return 'blue';
      case 'low': return 'gray';
      default: return 'gray';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = async () => {
    try {
      const params = { ...filters, limit: 1000 };
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await api.get('/email-forwarding/history', { params });
      const data = response.data.data;

      // Create CSV content
      const headers = ['Data', 'De', 'Assunto', 'Encaminhado Para', 'Status', 'Prioridade'];
      const rows = data.map(email => [
        formatDate(email.forwardingDetails?.forwardedAt || new Date()),
        email.originalEmail?.from || '',
        email.originalEmail?.subject || '',
        email.forwardedTo?.name || email.forwardedTo?.email || '',
        getStatusLabel(email.responseStatus?.status || 'pending'),
        email.metadata?.priority || 'normal'
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `email_forwarding_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      toast({
        title: 'Exportação concluída',
        description: 'O arquivo CSV foi baixado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      toast({
        title: 'Erro na exportação',
        description: 'Não foi possível exportar os dados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Filters */}
      <Box bg="gray.50" p={4} borderRadius="md">
        <HStack spacing={3} flexWrap="wrap">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Buscar por remetente, assunto..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              bg="white"
            />
          </InputGroup>

          <Select
            placeholder="Status"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            maxW="150px"
            bg="white"
          >
            <option value="pending">Pendente</option>
            <option value="replied">Respondido</option>
            <option value="ignored">Ignorado</option>
            <option value="in_progress">Em Progresso</option>
          </Select>

          <Select
            placeholder="Prioridade"
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            maxW="150px"
            bg="white"
          >
            <option value="urgent">Urgente</option>
            <option value="high">Alta</option>
            <option value="normal">Normal</option>
            <option value="low">Baixa</option>
          </Select>

          <Input
            type="date"
            placeholder="Data inicial"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            maxW="180px"
            bg="white"
          />

          <Input
            type="date"
            placeholder="Data final"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            maxW="180px"
            bg="white"
          />

          <Spacer />

          <Button
            leftIcon={<FiRefreshCw />}
            onClick={fetchEmails}
            variant="outline"
          >
            Atualizar
          </Button>

          <Button
            leftIcon={<FiDownload />}
            onClick={exportToCSV}
            colorScheme="green"
          >
            Exportar CSV
          </Button>
        </HStack>
      </Box>

      {/* Table */}
      {console.log('Rendering with emails:', emails, 'Loading:', loading)}
      {loading ? (
        <Center h="300px">
          <Spinner size="lg" color="blue.500" />
        </Center>
      ) : emails && emails.length > 0 ? (
        <>
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Data/Hora</Th>
                  <Th>De</Th>
                  <Th>Assunto</Th>
                  <Th>Encaminhado Para</Th>
                  <Th>Status</Th>
                  <Th>Prioridade</Th>
                  <Th>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {emails && emails.map((email) => (
                  <Tr key={email._id} _hover={{ bg: 'gray.50' }}>
                    <Td fontSize="sm">
                      {formatDate(email.forwardingDetails?.forwardedAt || new Date())}
                    </Td>
                    <Td fontSize="sm">
                      <Tooltip label={email.originalEmail?.from || ''}>
                        <Text isTruncated maxW="200px">
                          {email.originalEmail?.fromName || email.originalEmail?.from || 'Desconhecido'}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td fontSize="sm">
                      <Tooltip label={email.originalEmail?.subject || ''}>
                        <Text isTruncated maxW="300px">
                          {email.originalEmail?.subject || 'Sem assunto'}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td fontSize="sm">
                      {email.forwardedTo?.userId?.name || email.forwardedTo?.name || email.forwardedTo?.email || 'Desconhecido'}
                    </Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(email.responseStatus?.status || 'pending')}>
                        {getStatusLabel(email.responseStatus?.status || 'pending')}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={getPriorityColor(email.metadata?.priority || 'normal')}>
                        {email.metadata?.priority || 'normal'}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={1}>
                        <Tooltip label="Ver detalhes">
                          <IconButton
                            icon={<FiEye />}
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              console.log('Button clicked for email:', email._id);
                              viewEmailDetails(email._id);
                            }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination */}
          <Flex align="center" justify="center" mt={4}>
            <HStack spacing={2}>
              <IconButton
                icon={<FiChevronLeft />}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                isDisabled={page === 1}
                size="sm"
              />
              <Text fontSize="sm">
                Página {page} de {totalPages}
              </Text>
              <IconButton
                icon={<FiChevronRight />}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                isDisabled={page === totalPages}
                size="sm"
              />
            </HStack>
          </Flex>
        </>
      ) : (
        <Center h="300px">
          <Text color="gray.500">Nenhum email encontrado</Text>
        </Center>
      )}

      {/* Email Details Modal */}
      {console.log('Modal render check - selectedEmail:', selectedEmail, 'isOpen:', isOpen)}
      {selectedEmail && (
        <EmailDetailsModal
          isOpen={isOpen}
          onClose={() => {
            console.log('Modal closing...');
            setSelectedEmail(null);
            onClose();
          }}
          email={selectedEmail}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
    </VStack>
  );
};

export default ForwardingHistory;